import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { X, ChevronRight, ChevronLeft } from 'lucide-react';
import { useTutorial } from '../../contexts/TutorialContext';

interface Position {
  top: number;
  left: number;
  width: number;
  height: number;
}

export default function TourOverlay() {
  const navigate = useNavigate();
  const location = useLocation();
  const { tourActive, currentStep, currentTourStep, nextStep, prevStep, skipTour, tourSteps } = useTutorial();
  const [targetPosition, setTargetPosition] = useState<Position | null>(null);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});

  const updatePosition = useCallback(() => {
    if (!currentTourStep) return;

    const element = document.getElementById(currentTourStep.targetId);
    if (!element) {
      setTargetPosition(null);
      return;
    }

    const rect = element.getBoundingClientRect();
    const padding = 8;

    setTargetPosition({
      top: rect.top - padding,
      left: rect.left - padding,
      width: rect.width + padding * 2,
      height: rect.height + padding * 2,
    });

    const tooltipWidth = 320;
    const tooltipHeight = 180;
    let tooltipTop = 0;
    let tooltipLeft = 0;

    switch (currentTourStep.position) {
      case 'right':
        tooltipTop = rect.top + rect.height / 2 - tooltipHeight / 2;
        tooltipLeft = rect.right + 20;
        break;
      case 'left':
        tooltipTop = rect.top + rect.height / 2 - tooltipHeight / 2;
        tooltipLeft = rect.left - tooltipWidth - 20;
        break;
      case 'bottom':
        tooltipTop = rect.bottom + 20;
        tooltipLeft = rect.left + rect.width / 2 - tooltipWidth / 2;
        break;
      case 'top':
        tooltipTop = rect.top - tooltipHeight - 20;
        tooltipLeft = rect.left + rect.width / 2 - tooltipWidth / 2;
        break;
    }

    if (tooltipLeft < 16) tooltipLeft = 16;
    if (tooltipLeft + tooltipWidth > window.innerWidth - 16) {
      tooltipLeft = window.innerWidth - tooltipWidth - 16;
    }
    if (tooltipTop < 16) tooltipTop = 16;
    if (tooltipTop + tooltipHeight > window.innerHeight - 16) {
      tooltipTop = window.innerHeight - tooltipHeight - 16;
    }

    setTooltipStyle({
      position: 'fixed',
      top: tooltipTop,
      left: tooltipLeft,
      width: tooltipWidth,
    });
  }, [currentTourStep]);

  useEffect(() => {
    if (!tourActive || !currentTourStep) return;

    if (currentTourStep.route && location.pathname !== currentTourStep.route) {
      navigate(currentTourStep.route);
    }

    const timer = setTimeout(updatePosition, 100);

    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [tourActive, currentTourStep, location.pathname, navigate, updatePosition]);

  useEffect(() => {
    if (tourActive) {
      updatePosition();
    }
  }, [tourActive, currentStep, updatePosition]);

  if (!tourActive || !currentTourStep) return null;

  return (
    <div className="fixed inset-0 z-[100] pointer-events-auto">
      <svg
        className="absolute inset-0 w-full h-full"
        style={{ pointerEvents: 'none' }}
      >
        <defs>
          <mask id="spotlight-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {targetPosition && (
              <rect
                x={targetPosition.left}
                y={targetPosition.top}
                width={targetPosition.width}
                height={targetPosition.height}
                rx="12"
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(0, 0, 0, 0.75)"
          mask="url(#spotlight-mask)"
          style={{ pointerEvents: 'auto' }}
          onClick={skipTour}
        />
      </svg>

      {targetPosition && (
        <div
          className="absolute rounded-xl ring-4 ring-amber-400 ring-opacity-75 animate-pulse pointer-events-none"
          style={{
            top: targetPosition.top,
            left: targetPosition.left,
            width: targetPosition.width,
            height: targetPosition.height,
          }}
        />
      )}

      <div
        style={tooltipStyle}
        className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden animate-fade-in"
      >
        <div className="relative bg-gradient-to-r from-amber-600 to-amber-500 px-5 py-4 text-white">
          <button
            onClick={skipTour}
            className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-white/20 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-2 mb-1">
            <div className="flex gap-1">
              {tourSteps.map((_, idx) => (
                <div
                  key={idx}
                  className={`w-2 h-2 rounded-full transition-all ${
                    idx === currentStep
                      ? 'bg-white scale-125'
                      : idx < currentStep
                      ? 'bg-white/80'
                      : 'bg-white/40'
                  }`}
                />
              ))}
            </div>
          </div>
          <h3 className="font-bold text-lg">{currentTourStep.title}</h3>
        </div>

        <div className="p-5">
          <p className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed mb-5">
            {currentTourStep.description}
          </p>

          <div className="flex items-center justify-between">
            <button
              onClick={prevStep}
              disabled={currentStep === 0}
              className={`flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                currentStep === 0
                  ? 'text-slate-300 dark:text-slate-600 cursor-not-allowed'
                  : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
              }`}
            >
              <ChevronLeft className="w-4 h-4" />
              Vorige
            </button>

            <button
              onClick={nextStep}
              className="flex items-center gap-1 px-5 py-2 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700 transition-colors"
            >
              {currentStep === tourSteps.length - 1 ? (
                'Afronden'
              ) : (
                <>
                  Volgende
                  <ChevronRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
